<script lang="ts" setup>
import type { OfferMedium, RecommendationsResponse } from '~/types/Catalog';
import type { FreeDeliveryTerm } from '~/types/Cart';

definePageMeta({ breadcrumb: 'Корзина' });
useHead({ title: `Корзина – Hellride.ru` });

const cartStore = useCartStore();
const catalogStore = useCatalogStore();
const siteStore = useSiteStore();

await cartStore.getCart();

const { cart, selectedCartItems, isCartLoaded } = storeToRefs(cartStore);
const { yandexSplitThreshold } = storeToRefs(siteStore);
const { isMobile } = useBreakpoints();

const availableItems = computed(() => cart.value.items.filter((item) => item.available_count !== 0));
const unavailableItems = computed(() => cart.value.items.filter((item) => item.available_count === 0));

const allChecked = computed(() => selectedCartItems.value.length === availableItems.value.length);

const isCheckoutUnavailable = computed(
  () =>
    selectedCartItems.value.length === 0 ||
    selectedCartItems.value.some((item) => item.available_count < item.count) ||
    isCartLoaded.value === false,
);

const isShowYandexSplitModal = ref(false);

const proceedToCheckoutText = computed(() => {
  return selectedCartItems.value.length === 0 ? 'Выберите товары' : 'Перейти к оформлению';
});

const isLoading = ref({ selected: false, unavailable: false });
const freeDeliveryTerms = ref<FreeDeliveryTerm[]>([]);

async function getFreeDeliveryTerms() {
  freeDeliveryTerms.value = await cartStore.getFreeDeliveryTerms();
}

getFreeDeliveryTerms();

const freeDeliveryAmount = computed(() => {
  if (freeDeliveryTerms.value.length) {
    freeDeliveryTerms.value.sort((a, b) => a.amount - b.amount);

    return freeDeliveryTerms.value[0].amount - cart.value.calculate.total;
  }
  return 0;
});

const yandexSplitAmount = computed(() => {
  if (freeDeliveryAmount.value <= 0 && yandexSplitThreshold.value) {
    return yandexSplitThreshold.value - cart.value.calculate.total;
  }
  return 0;
});

const { sendPageViewOffersEventToDataLayer } = useGtmModule();

const recommendationsBlock = ref<RecommendationsResponse>();
const recommendedOffers = ref<OfferMedium[]>();

onMounted(async () => {
  if (import.meta.client) {
    await sendPageViewOffersEventToDataLayer(
      'view_cart',
      cart.value.items.map((item) => item.data),
    );

    if (window.r46) {
      for (const block of siteStore.recommendationBlocks) {
        if (block.type === 'cart') {
          window.r46('recommend', block.block_identifier, {}, async function (response: RecommendationsResponse) {
            if (!response.recommends.length) return;

            recommendationsBlock.value = { ...response, block_identifier: block.block_identifier };
            recommendedOffers.value = await catalogStore.getOffersList(response.recommends.join(','));
          });
        }
      }
    }
  }
});

async function checkAllItems() {
  if (selectedCartItems.value.length === availableItems.value.length) {
    await cartStore.changeSelectedCartItems(
      availableItems.value.map((i) => i.id),
      true,
    );
  } else {
    await cartStore.changeSelectedCartItems(
      availableItems.value.map((i) => i.id),
      false,
    );
  }
}

async function removeSelected() {
  isLoading.value.selected = true;

  await cartStore.removeFromCart(selectedCartItems.value.map((i) => i.id));
  await cartStore.getCart();

  isLoading.value.selected = false;
}

async function removeUnavailable() {
  isLoading.value.unavailable = true;

  await cartStore.removeFromCart(unavailableItems.value.map((i) => i.id));
  await cartStore.getCart();

  isLoading.value.unavailable = false;
}
</script>

<template>
  <div class="cart-page">
    <template v-if="cart.items.length">
      <BreadcrumbsBlock />

      <div class="cart-page__header">
        <h1>Корзина</h1>

        <CartShareBtn v-if="isMobile && selectedCartItems.length" />
      </div>

      <div class="cart-page__content">
        <section class="cart-page__items-list">
          <CardBlock
            v-if="freeDeliveryAmount && freeDeliveryAmount > 0"
            class="flex items-center gap-3 md:mb-3"
          >
            <SvgoShipment
              :font-controlled="false"
              class="size-8"
              filled
            />

            <div class="text-xs sm:text-sm">
              Добавьте товаров ещё на<strong>&nbsp;{{ freeDeliveryAmount }}&nbsp;₽</strong> и&nbsp;доставка
              будет&nbsp;<strong>бесплатной</strong>
            </div>
          </CardBlock>

          <CardBlock
            v-if="yandexSplitAmount && yandexSplitAmount > 0"
            class="flex items-center gap-3 md:mb-3"
          >
            <SvgoYandexSplitPacman
              :font-controlled="false"
              class="size-8"
              filled
            />

            <div class="text-xs sm:text-sm">
              Добавьте товаров ещё на<strong>&nbsp;{{ yandexSplitAmount }}&nbsp;₽</strong> и станет доступна оплата в
              Сплит
            </div>
          </CardBlock>

          <div
            v-if="availableItems.length"
            class="flex items-center justify-between gap-3 p-3"
          >
            <div class="flex flex-grow items-center justify-between md:justify-start gap-3">
              <div class="cart-page__select-all-checkbox">
                <PrimeCheckbox
                  :binary="true"
                  :class="{ 'cart-page__select-all-checkbox--mobile': isMobile }"
                  :model-value="allChecked"
                  input-id="check-all-items"
                  @click="checkAllItems"
                />

                <label
                  class="cursor-pointer"
                  for="check-all-items"
                >
                  Выбрать все
                </label>
              </div>

              <ConfirmationDropdown
                v-if="selectedCartItems.length"
                cancelBtnText="Нет"
                okBtnText="Да"
                text="Удалить из корзины?"
                @confirm="removeSelected"
              >
                <button class="flex items-center gap-1 text-hell-black-80 hover:text-hell-red transition">
                  <SvgoSpinner
                    v-if="isLoading.selected"
                    class="size-4 animate-spin"
                    title="Идет загрузка..."
                  />

                  <SvgoCross
                    v-else
                    :font-controlled="false"
                    class="size-4"
                    filled
                  />

                  Удалить выбранные
                </button>
              </ConfirmationDropdown>
            </div>

            <CartShareBtn v-if="isMobile === false && selectedCartItems.length" />
          </div>

          <CartListItem
            v-for="item in availableItems"
            :key="item.id"
            :cart-item="item"
            :is-error="!!item.available_count && item.count > item.available_count"
          />

          <template v-if="unavailableItems.length">
            <div class="flex items-center justify-between mt-2">
              <div class="font-bold text-base">Недоступные товары:</div>

              <button
                class="flex items-center gap-1 text-hell-black-80 hover:text-hell-red transition"
                @click="removeUnavailable"
              >
                <SvgoSpinner
                  v-if="isLoading.unavailable"
                  class="size-4 animate-spin"
                  title="Идет загрузка..."
                />

                <SvgoCross
                  v-else
                  :font-controlled="false"
                  class="size-4"
                  filled
                />

                Удалить все
              </button>
            </div>

            <CartListItem
              v-for="item in unavailableItems"
              :key="item.id"
              :cart-item="item"
              is-unavailable
            />
          </template>
        </section>

        <CardBlock class="cart-page__summary-block">
          <Transition name="loader">
            <div
              v-if="!isCartLoaded"
              class="cart-page__summary-block-loading-overlay"
            >
              <SvgoSpinner
                :font-controlled="false"
                class="size-10 animate-spin"
                title="Идет загрузка..."
              />
            </div>
          </Transition>

          <PromoCodeInput />

          <CartDetailsBlock />

          <div class="cart-page__continue-block">
            <YandexPaySplitBadge
              v-if="!isMobile"
              v-model:is-show-split-modal="isShowYandexSplitModal"
              :amount="cart.calculate.total"
              with-shadow
            >
              <TheBtn
                :disabled="isCheckoutUnavailable"
                class="my-2"
                size="md"
                to="/checkout"
              >
                {{ proceedToCheckoutText }}
              </TheBtn>
            </YandexPaySplitBadge>

            <TheBtn
              :disabled="isCheckoutUnavailable"
              class="w-full"
              to="/checkout"
            >
              {{ proceedToCheckoutText }}
            </TheBtn>
          </div>
        </CardBlock>

        <YandexPaySplitBadge
          v-if="isMobile"
          v-model:is-show-split-modal="isShowYandexSplitModal"
          :amount="cart.calculate.total"
          with-shadow
        >
          <TheBtn
            :disabled="isCheckoutUnavailable"
            class="my-2"
            size="md"
            to="/checkout"
          >
            {{ proceedToCheckoutText }}
          </TheBtn>
        </YandexPaySplitBadge>

        <ProductCardsGallery
          v-if="recommendedOffers?.length"
          :offers="recommendedOffers"
          :recommendations-block-id="recommendationsBlock?.block_identifier"
          :title="`<h2>${recommendationsBlock?.title}</h2>`"
          class="!p-0 !mt-4 sm:!hidden"
        />

        <div class="cart-page__continue-block-mobile">
          <div
            v-if="selectedCartItems.length > 0"
            class="flex flex-col flex-shrink-0"
          >
            <span
              v-if="cart.calculate.price_without_discounts < cart.calculate.total"
              class="text-hell-black-60 line-through"
            >
              {{ cart.calculate.price_without_discounts }} ₽
            </span>

            <span class="text-lg font-bold">{{ cart.calculate.total }} ₽</span>
          </div>

          <TheBtn
            :disabled="isCheckoutUnavailable"
            class="flex-grow"
            size="md"
            to="/checkout"
          >
            {{ proceedToCheckoutText }}
          </TheBtn>
        </div>
      </div>

      <ProductCardsGallery
        v-if="recommendedOffers?.length"
        :offers="recommendedOffers"
        :recommendations-block-id="recommendationsBlock?.block_identifier"
        :title="`<h2>${recommendationsBlock?.title}</h2>`"
        class="!px-0 !mt-10 !hidden sm:!flex"
      />
    </template>

    <section
      v-else
      class="cart-page--empty"
    >
      <h2 class="text-center">
        В корзине пока пусто,<br />
        но в каталоге 1000+ товаров
      </h2>

      <TheBtn
        class="max-w-xs"
        to="/catalog"
      >
        Перейти в каталог
      </TheBtn>

      <CartShareBtn class="w-0 h-0 overflow-hidden" />
    </section>
  </div>
</template>

<style lang="scss">
.cart-page {
  @include inner-content;

  gap: 16px;
  height: 100%;

  &__header {
    display: flex;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
  }

  &__select-all-checkbox {
    display: flex;
    gap: 8px;
    align-items: center;

    &--mobile {
      &.p-checkbox .p-checkbox-box {
        width: 14px;
        height: 14px;
        margin-top: 2px;
        box-shadow: none;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    @include screen-lg {
      flex-direction: row;
      gap: 64px;
    }
  }

  &__items-list {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 0.5rem;
    font-size: $text-xs;

    @include screen-md {
      font-size: $text-sm;
    }
  }

  &__summary-block {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-self: center;
    width: 100%;

    @include screen-sm {
      max-width: 480px;
    }

    @include screen-lg {
      flex-shrink: 0;
      gap: 1.5rem;
      align-self: flex-start;
      width: 360px;
      padding: 16px;
      font-size: inherit;
    }

    @include screen-xl {
      width: 480px;
    }
  }

  &__summary-block-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    background-color: rgb(255 255 255 / 80%);
  }

  &__continue-block {
    display: none;

    @include screen-sm {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
  }

  &__continue-block-mobile {
    position: sticky;
    bottom: 74px;
    z-index: 45;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
    width: 100vw;
    margin: 0 -12px;
    padding: 12px;
    background-color: $white;
    box-shadow: 0 0 20px rgb(0 0 0 / 15%);

    @include screen-sm {
      display: none;
    }
  }

  &--empty {
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    margin-bottom: 24px;
    padding: 24px 12px;
    border: 2px dotted $hell-black-60;
    border-radius: 32px;
  }
}
</style>
